/**
 * Affiliate Payouts Tab
 *
 * Lists payout requests filtered by status. Action buttons depend on status:
 * requested → approve / reject; approved → mark paid / reject;
 * paid / rejected → terminal (no actions). Reject accepts an optional note.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, X, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import {
  affiliateApi,
  type PayoutItem,
  type PayoutStatus,
  type PayoutAction,
} from '@/lib/api/affiliate';
import { parseBackendDateTime } from '@/lib/format/datetime';

function statusVariant(
  status: PayoutStatus,
): 'warning' | 'info' | 'success' | 'destructive' {
  switch (status) {
    case 'approved':
      return 'info';
    case 'paid':
      return 'success';
    case 'rejected':
      return 'destructive';
    default:
      return 'warning';
  }
}

function formatDate(ts: string): string {
  return parseBackendDateTime(ts).toLocaleString();
}

function formatAmount(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

export function AffiliatePayoutsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<PayoutStatus>('requested');
  const [rejectForm, setRejectForm] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['affiliate-payouts', status],
    queryFn: () => affiliateApi.listPayouts(status),
  });

  const closeForm = () => {
    setRejectForm(null);
    setNote('');
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['affiliate-payouts'] });
    queryClient.invalidateQueries({ queryKey: ['affiliate-affiliates'] });
  };

  const reviewMutation = useMutation({
    mutationFn: ({ id, action, noteText }: { id: string; action: PayoutAction; noteText?: string }) =>
      affiliateApi.reviewPayout(id, { action, note: noteText }),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(
        variables.action === 'approve'
          ? t('affiliatePage.payoutApproved')
          : variables.action === 'mark_paid'
            ? t('affiliatePage.payoutMarkedPaid')
            : t('affiliatePage.payoutRejected'),
      );
      closeForm();
    },
    onError: (err: Error) => {
      toast.error(`${t('affiliatePage.actionFailed')}: ${err.message}`);
    },
  });

  const isMutating = reviewMutation.isPending;

  const statusOptions = [
    { value: 'requested', label: t('affiliatePage.payoutStatusRequested') },
    { value: 'approved', label: t('affiliatePage.payoutStatusApproved') },
    { value: 'paid', label: t('affiliatePage.payoutStatusPaid') },
    { value: 'rejected', label: t('affiliatePage.payoutStatusRejected') },
  ];

  const statusLabel = (s: PayoutStatus) =>
    s === 'approved'
      ? t('affiliatePage.payoutStatusApproved')
      : s === 'paid'
        ? t('affiliatePage.payoutStatusPaid')
        : s === 'rejected'
          ? t('affiliatePage.payoutStatusRejected')
          : t('affiliatePage.payoutStatusRequested');

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('affiliatePage.payoutsTitle')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{t('affiliatePage.payoutsDesc')}</p>
        </div>
        <div className="w-48">
          <Select
            label={t('affiliatePage.filterStatus')}
            value={status}
            onChange={(e) => setStatus(e.target.value as PayoutStatus)}
            options={statusOptions}
          />
        </div>
      </div>

      {error && (
        <Card className="mb-4 border-destructive">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
            <p className="text-destructive text-sm">{(error as Error).message}</p>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : !data?.length ? (
        <Card>
          <p className="text-center text-muted-foreground py-8">
            {t('affiliatePage.noPayouts')}
          </p>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">{t('affiliatePage.colAffiliateId')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colAmount')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colStatus')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colNote')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colRequestedAt')}</th>
                  <th className="px-4 py-2 text-right">{t('affiliatePage.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((payout: PayoutItem) => {
                  const isTerminal = payout.status === 'paid' || payout.status === 'rejected';
                  return (
                    <tr key={payout.id} className="border-b border-border align-top">
                      <td className="px-4 py-3">
                        <span
                          className="font-mono text-xs text-foreground"
                          title={payout.affiliate_id}
                        >
                          {payout.affiliate_id}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-foreground">
                        {formatAmount(payout.amount_cents)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusVariant(payout.status)}>
                          {statusLabel(payout.status)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate">
                        {payout.note || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {formatDate(payout.requested_at)}
                      </td>
                      <td className="px-4 py-3">
                        {isTerminal ? (
                          <div className="text-right text-muted-foreground">—</div>
                        ) : (
                          <div className="flex flex-col items-end gap-2">
                            {rejectForm === payout.id ? (
                              <div className="w-72 rounded-lg border border-border p-3 bg-muted/30">
                                <Input
                                  label={t('affiliatePage.rejectNoteLabel')}
                                  value={note}
                                  onChange={(e) => setNote(e.target.value)}
                                  helperText={t('affiliatePage.rejectNoteHelp')}
                                />
                                <div className="flex justify-end gap-2 mt-3">
                                  <Button variant="outline" size="sm" onClick={closeForm}>
                                    {t('affiliatePage.cancel')}
                                  </Button>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={isMutating}
                                    onClick={() =>
                                      reviewMutation.mutate({
                                        id: payout.id,
                                        action: 'reject',
                                        noteText: note.trim() || undefined,
                                      })
                                    }
                                  >
                                    {t('affiliatePage.confirm')}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                {payout.status === 'requested' && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={isMutating}
                                    onClick={() =>
                                      reviewMutation.mutate({ id: payout.id, action: 'approve' })
                                    }
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    {t('affiliatePage.approve')}
                                  </Button>
                                )}
                                {payout.status === 'approved' && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={isMutating}
                                    onClick={() =>
                                      reviewMutation.mutate({ id: payout.id, action: 'mark_paid' })
                                    }
                                  >
                                    <Banknote className="w-4 h-4 mr-1" />
                                    {t('affiliatePage.markPaid')}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    closeForm();
                                    setRejectForm(payout.id);
                                  }}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  {t('affiliatePage.reject')}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={() => refetch()}>
          {t('affiliatePage.refresh')}
        </Button>
      </div>
    </div>
  );
}
